import { useEffect, useState } from 'react';
import { getSocket } from '../lib/socket';

export type SocketStatus = 'connected' | 'reconnecting' | 'disconnected';

/**
 * Reports the current Socket.IO connection state for the singleton in
 * lib/socket.ts. Used by the TopBar live indicator so operators can
 * see at a glance whether real-time events are flowing. Mirrors the
 * three states the socket transitions through:
 *
 *   connect          → connected
 *   reconnect_attempt → reconnecting (between drops)
 *   disconnect       → disconnected (if no reconnect in flight)
 */
export function useSocketStatus(): SocketStatus {
  const [status, setStatus] = useState<SocketStatus>(() =>
    getSocket().connected ? 'connected' : 'reconnecting',
  );

  useEffect(() => {
    const s = getSocket();
    function onConnect() {
      setStatus('connected');
    }
    function onReconnecting() {
      setStatus('reconnecting');
    }
    function onDisconnect(reason: string) {
      // 'io client disconnect' and 'io server disconnect' don't auto-
      // reconnect; everything else does, so we stay in 'reconnecting'.
      if (reason === 'io client disconnect' || reason === 'io server disconnect') {
        setStatus('disconnected');
      } else {
        setStatus('reconnecting');
      }
    }
    function onReconnectFailed() {
      setStatus('disconnected');
    }
    s.on('connect', onConnect);
    s.io.on('reconnect_attempt', onReconnecting);
    s.io.on('reconnect_failed', onReconnectFailed);
    s.on('disconnect', onDisconnect);
    // Sync once after attach in case we mounted between events.
    setStatus(s.connected ? 'connected' : 'reconnecting');
    return () => {
      s.off('connect', onConnect);
      s.io.off('reconnect_attempt', onReconnecting);
      s.io.off('reconnect_failed', onReconnectFailed);
      s.off('disconnect', onDisconnect);
    };
  }, []);

  return status;
}
